// 📄 pages/Proyectos/solucionesConfig.ts
// Fuente única de metadata por solución de SAP Ariba: la usan el selector
// de "Nuevo/Editar Proyecto", el sidebar del proyecto, el picker de
// ProjectSolutionsHome y el contenido de SolucionPlaceholder. Agregar una
// solución nueva es agregar una entrada acá, no tocar 4 archivos distintos.
//
// "commerce" es un caso especial: tiene su propia experiencia completa
// (CommerceHome + rutas de integración) en vez de SolucionPlaceholder, así
// que sus campos de contenido (sectionTitle/cards/etc.) no se usan.

export interface SolucionCard {
  title: string;
  desc: string;
}

export interface SolucionMeta {
  slug: string;
  path: string; // segmento de ruta bajo /proyecto/:projectId/
  label: string;
  icon: string;
  tagline: string;
  sectionTitle: string;
  description: string;
  cards: SolucionCard[];
  accentClass: string;
}

export const SOLUCIONES: Record<string, SolucionMeta> = {
  commerce: {
    slug: 'commerce',
    path: 'commerce',
    label: 'Commerce Automation',
    icon: '📄',
    tagline: 'Procesamiento de facturas, órdenes y documentos electrónicos.',
    sectionTitle: '',
    description: '',
    cards: [],
    accentClass: 'text-blue-400'
  },
  slp: {
    slug: 'slp',
    path: 'slp',
    label: 'SLP',
    icon: '🧑‍💼',
    tagline: 'Gestiona el ciclo de vida de tus proveedores, desde el registro hasta el desempeño.',
    sectionTitle: '¿Qué es SLP?',
    description: 'Supplier Lifecycle and Performance (SLP) es el módulo central para manejar la relación con tus socios de negocio. Permite segmentar proveedores, gestionar su calificación y asegurar el cumplimiento de normativas globales.',
    cards: [
      { title: '✅ Registro y Calificación', desc: 'Automatiza la recolección de datos y documentos de nuevos proveedores.' },
      { title: '⭐ Gestión del Desempeño', desc: 'Evalúa métricas clave y niveles de servicio de forma centralizada.' }
    ],
    accentClass: 'text-amber-400'
  },
  sourcing: {
    slug: 'sourcing',
    path: 'sourcing',
    label: 'Sourcing',
    icon: '📊',
    tagline: 'Automatización de licitaciones y subastas estratégicas.',
    sectionTitle: 'Eventos y Negociación',
    description: 'Gestiona eventos de sourcing de punta a punta: desde la definición de requerimientos hasta la adjudicación final.',
    cards: [
      { title: 'RFX Management', desc: 'Creación de RFI, RFP y RFQ para selección de proveedores.' },
      { title: 'Real-time Auctions', desc: 'Gestión de subastas inversas para optimización de ahorros.' }
    ],
    accentClass: 'text-emerald-400'
  },
  buying: {
    slug: 'buying',
    path: 'buying',
    label: 'Buying',
    icon: '🛒',
    tagline: 'Experiencia de compra guiada y gestión de catálogos.',
    sectionTitle: 'Compras y Catálogos',
    description: 'Simplifica la experiencia de compra de los usuarios finales, dentro de las políticas y catálogos aprobados.',
    cards: [
      { title: 'Guided Buying', desc: 'Interfaz simplificada para que los usuarios compren bajo políticas.' },
      { title: 'PunchOut & Catalogs', desc: 'Integración directa con catálogos de proveedores externos.' }
    ],
    accentClass: 'text-amber-500'
  },
  contracts: {
    slug: 'contracts',
    path: 'contracts',
    label: 'Contracts',
    icon: '📑',
    tagline: 'Gestión del ciclo de vida de contratos y cumplimiento legal.',
    sectionTitle: 'Contratos y Cumplimiento',
    description: 'Centraliza la creación, negociación y seguimiento de contratos, con trazabilidad completa de cambios.',
    cards: [
      { title: 'Repository', desc: 'Almacenamiento centralizado y búsqueda avanzada de documentos legales.' },
      { title: 'Compliance', desc: 'Seguimiento de hitos, renovaciones automáticas y alertas de expiración.' }
    ],
    accentClass: 'text-purple-400'
  },
  cig: {
    slug: 'cig',
    path: 'cig',
    label: 'CIG (Managed Gateway)',
    icon: '🔄',
    tagline: 'Transformación, mapeos e integración entre Ariba y sistemas ERP / No-ERP.',
    sectionTitle: 'Integración vía Managed Gateway',
    description: 'SAP Integration Suite - Managed Gateway for Spend Management conecta Ariba con el ERP (o sistemas No-ERP) transformando y enrutando documentos.',
    cards: [
      { title: 'Transformación de Documentos', desc: 'Mapeo y conversión de formatos entre Ariba Network y el ERP/No-ERP.' },
      { title: 'Monitoreo Centralizado', desc: 'Trazabilidad de mensajes e incidencias a través de SAP Integration Suite.' }
    ],
    accentClass: 'text-sky-400'
  }
};

// Todas menos "commerce", que tiene su propia experiencia dedicada.
export const OTRAS_SOLUCIONES: SolucionMeta[] = Object.values(SOLUCIONES).filter(s => s.slug !== 'commerce');

export const SOLUCION_OPTIONS = Object.values(SOLUCIONES).map(s => ({ value: s.slug, label: s.label, icon: s.icon }));
